import { ActionIcon, Alert, Box, Button, Card, Divider, Group, Menu, Paper, SegmentedControl, Select, Stack, Text, Tooltip, Typography } from "@mantine/core";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { type ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_STYLE } from "../../common/editor-theme";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { IconChevronDown, IconDownload, IconUpload, IconX } from "../../icons";
import { applyFormat, EDITOR_EXTENSIONS, fileDropHandlers, replaceDocument } from "./editor";
import { DEFAULT_FLAVOUR, FLAVOUR_OPTIONS, isFlavour } from "./flavours";
import type { FormatKind } from "./format";
import { ACCEPT, message, readDocument } from "./open";
import { renderMarkdown } from "./render";
import { SAMPLE_DOCUMENT } from "./sample";
import { SAVE_FORMATS, saveDocument, type SaveKind } from "./save";
import { FORMAT_GROUPS, shortcutLabel } from "./toolbar";
import { DEFAULT_VIEW, isView, VIEWS } from "./views";

export default function Markdown() {
  const initialState = useInitialHashState<{
    value?: string;
    flavour?: string;
    view?: string;
  }>();

  // Uncontrolled, as in /json: feeding the text back in would replace the document and send the caret to the end.
  const [initialValue] = useState(() => initialState?.value ?? SAMPLE_DOCUMENT);
  const valueRef = useRef(initialValue);
  const editorRef = useRef<EditorView | null>(null);

  const [flavour, setFlavour] = useState(() =>
    isFlavour(initialState?.flavour) ? initialState.flavour : DEFAULT_FLAVOUR
  );
  const [view, setView] = useState(() => isView(initialState?.view) ? initialState.view : DEFAULT_VIEW);
  // What the preview was last drawn from, which trails the typing by a beat: parsing and sanitising a long document
  // on every keystroke is the one thing here that would make the editor feel slow.
  const [previewed, setPreviewed] = useState(initialValue);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const html = useMemo(() => renderMarkdown(previewed, flavour), [previewed, flavour]);

  const syncShareState = useRegisterShareState(() => ({ value: valueRef.current, flavour, view }));

  // `window.markdownEditor` is how devtools and the Playwright specs read the document; drop it on the way out, with
  // whatever the last keystroke left waiting to be drawn.
  useEffect(() => () => {
    self.markdownEditor = undefined;
    if (previewTimer.current !== null) clearTimeout(previewTimer.current);
  }, []);

  const handleChange = useCallback((next: string) => {
    valueRef.current = next;
    syncShareState();

    if (previewTimer.current !== null) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      previewTimer.current = null;
      setPreviewed(valueRef.current);
    }, PREVIEW_DELAY);
  }, [syncShareState]);

  const handleFormat = useCallback((kind: FormatKind) => applyFormat(editorRef.current, kind), []);

  // Saved from the editor, which the preview trails; the wait shown is for the pictures and for the PDF's writer.
  const handleSave = useCallback(async (kind: SaveKind) => {
    setSaving(true);
    try {
      await saveDocument(kind, valueRef.current, flavour);
    } catch (error) {
      setFailure({ title: "That document was not saved", message: message(error) });
    } finally {
      setSaving(false);
    }
  }, [flavour]);

  const handleFile = useCallback(async (file: File | null) => {
    if (!file) return;
    try {
      const text = await readDocument(file);
      setFailure(null);
      replaceDocument(editorRef.current, text);
    } catch (error) {
      setFailure({ title: "That file did not open", message: message(error) });
    }
  }, []);

  // Memoised on nothing, as the array itself is: a new one reconfigures the running editor, and the handler it holds
  // reads everything it needs off refs and setters that never change.
  const extensions = useMemo(
    () => [...EDITOR_EXTENSIONS, fileDropHandlers(setDragging, (file) => void handleFile(file))],
    [handleFile],
  );

  return (
    <Stack flex={1} className="fill-screen" gap="md">
      <UtilityTitle directory="markdown">Markdown</UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Group align="flex-end" gap="xl" justify="space-between">
          <Group align="flex-end" gap="md">
            <Select
              label="Flavour"
              data={FLAVOUR_OPTIONS}
              value={flavour}
              onChange={(value) => isFlavour(value) && setFlavour(value)}
              allowDeselect={false}
              w={260}
            />
            {/* A label rather than a button with a handler, so the box is the picker and the input stays reachable. */}
            <Button
              component="label"
              variant="default"
              leftSection={<IconUpload size="1rem" stroke={1.5} />}
            >
              Open file
              <input
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  void handleFile(event.currentTarget.files?.item(0) ?? null);
                  // Cleared, or choosing the same file twice in a row is a change the input does not report.
                  event.currentTarget.value = "";
                }}
              />
            </Button>

            <Menu shadow="md" position="bottom-start" withinPortal>
              <Menu.Target>
                <Button
                  variant="default"
                  loading={saving}
                  leftSection={<IconDownload size="1rem" stroke={1.5} />}
                  rightSection={<IconChevronDown size="0.9rem" stroke={1.5} />}
                >
                  Download
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {SAVE_FORMATS.map(({ value, label, Icon, note }) => (
                  <Menu.Item
                    key={value}
                    leftSection={<Icon size="1rem" stroke={1.5} />}
                    onClick={() => void handleSave(value)}
                  >
                    {label}
                    <Text component="span" display="block" size="xs" c="dimmed">{note}</Text>
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
          <SegmentedControl
            value={view}
            onChange={(value) => isView(value) && setView(value)}
            aria-label="Which halves of the page are showing"
            data={VIEWS.map(({ value, label, Icon }) => ({
              value,
              label: (
                <Group gap={6} wrap="nowrap" justify="center">
                  <Icon size="1rem" stroke={1.5} />
                  {label}
                </Group>
              ),
            }))}
          />
        </Group>
      </Card>

      {failure && (
        <Alert
          color="red"
          icon={<IconX size="1rem" />}
          title={failure.title}
          withCloseButton
          onClose={() => setFailure(null)}
        >
          {failure.message}
        </Alert>
      )}

      {
        /* Both halves stay mounted whichever view is showing: unmounting the editor would take the document, the undo
          history and the caret with it, and a view is a way of looking at the page rather than a different page. */
      }
      <Box className="markdown-panes" data-view={view}>
        <Paper
          withBorder
          shadow="sm"
          radius="md"
          className="markdown-editor-pane"
          data-dragging={dragging || undefined}
        >
          <Group className="markdown-toolbar" gap={2} role="toolbar" aria-label="Formatting">
            {FORMAT_GROUPS.map((group, index) => (
              <Fragment key={group[0].kind}>
                {index > 0 && <Divider orientation="vertical" mx={4} />}
                {group.map(({ kind, label, Icon, key }) => (
                  <Tooltip key={kind} label={key ? `${label} (${shortcutLabel(key)})` : label} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={label}
                      onClick={() =>
                        handleFormat(kind)}
                    >
                      <Icon size="1.1rem" stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                ))}
              </Fragment>
            ))}
          </Group>

          <Box className="markdown-editor-host">
            <Box style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
              <CodeMirror
                value={initialValue}
                // A percentage height only resolves against a definite one, so the wrapper matches the editor.
                height="100%"
                style={EDITOR_STYLE}
                theme="dark"
                extensions={extensions}
                onCreateEditor={(editor) => {
                  editorRef.current = editor;
                  self.markdownEditor = editor;
                }}
                onChange={handleChange}
              />
            </Box>
          </Box>
        </Paper>

        <Paper withBorder shadow="sm" radius="md" className="markdown-preview-pane">
          {/* Sanitised where it is rendered, in render.ts, since a shared document is one somebody else wrote. */}
          <Typography className="markdown-preview" dangerouslySetInnerHTML={{ __html: html }} />
        </Paper>
      </Box>
    </Stack>
  );
}

// Long enough that a run of keystrokes redraws the preview once, short enough to read as the next thing that happens.
const PREVIEW_DELAY = 120;

// What went wrong and what somebody was doing at the time, a file that will not open and a document that will not
// save being two different sentences.
interface Failure {
  title: string;
  message: string;
}

declare global {
  var markdownEditor: EditorView | undefined;
}
